import React, { useState, useEffect, useMemo } from 'react';
import { budgetsApi, settingsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { sortAlphabetically, sortFiscalYearsNewestFirst, generateExportFilename, getBuCode } from '../lib/namingUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import CommentsSidePanel from '../components/CommentsSidePanel';
import { 
  TrendingUp, 
  TrendingDown, 
  Download,
  Calendar,
  FileSpreadsheet,
  FileText,
  Building2,
  ChevronDown,
  ChevronRight,
  Layers,
  RefreshCw,
  Filter,
  EyeOff,
  ArrowLeft,
  BarChart3,
  Eye,
  MessageSquare,
  LayoutGrid,
  List,
  Grid
} from 'lucide-react';

const MONTHS = ['jul', 'aug', 'sep', 'oct', 'nov', 'dec', 'jan', 'feb', 'mar', 'apr', 'may', 'jun'];
const MONTH_NAMES = {
  jul: 'July', aug: 'August', sep: 'September', oct: 'October', nov: 'November', dec: 'December',
  jan: 'January', feb: 'February', mar: 'March', apr: 'April', may: 'May', jun: 'June'
};
const MONTH_LABELS = {
  jul: 'Jul', aug: 'Aug', sep: 'Sep', oct: 'Oct', nov: 'Nov', dec: 'Dec',
  jan: 'Jan', feb: 'Feb', mar: 'Mar', apr: 'Apr', may: 'May', jun: 'Jun'
};

const CATEGORY_COLORS = {
  'Sales': { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
  'Purchases': { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800' },
  'Overhead': { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
  'Salaries': { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
  'Management Fees': { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800' },
  'Other Expenses': { bg: 'bg-slate-50 dark:bg-zinc-800/50', text: 'text-slate-700 dark:text-zinc-400', border: 'border-slate-200 dark:border-zinc-700' },
};

const TIME_PERIODS = [
  { value: 'all', label: 'Full Year' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'q1', label: 'Q1 (Jul-Sep)' },
  { value: 'q2', label: 'Q2 (Oct-Dec)' },
  { value: 'q3', label: 'Q3 (Jan-Mar)' },
  { value: 'q4', label: 'Q4 (Apr-Jun)' },
];

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  const absValue = Math.abs(value);
  const formatted = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(absValue);
  return value < 0 ? `(${formatted})` : formatted;
};

// Format currency as expense - always shows in parentheses using absolute value
// Use this for expense rows (Purchases, Overhead, etc.) to ensure uniform display
const formatCurrencyAsExpense = (value) => {
  if (value === null || value === undefined) return '-';
  const absValue = Math.abs(value);
  const formatted = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(absValue);
  return `(${formatted})`;
};

// Summary Card Component - Now clickable
const SummaryCard = ({ label, budget, actual, isDark, highlight, isExpense, onClick, isActive }) => {
  const variance = actual - budget;
  const variancePercent = budget !== 0 ? ((variance / budget) * 100).toFixed(1) : 0;
  const isPositive = isExpense ? variance <= 0 : variance >= 0;
  
  return (
    <Card 
      className={`border shadow-none rounded-sm cursor-pointer transition-all hover:shadow-md ${
        isActive 
          ? isDark ? 'ring-2 ring-emerald-500 bg-emerald-900/30' : 'ring-2 ring-emerald-500 bg-emerald-50'
          : highlight 
            ? isDark ? 'bg-emerald-900/20 border-emerald-800 hover:border-emerald-600' : 'bg-emerald-50 border-emerald-200 hover:border-emerald-400' 
            : isDark ? 'bg-zinc-900 border-zinc-800 hover:border-zinc-600' : 'border-slate-200 hover:border-slate-400'
      }`}
      onClick={onClick}
      data-testid={`summary-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-3">
        <p className={`text-xs font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{label}</p>
        <div className="mt-1 grid grid-cols-2 gap-2">
          <div>
            <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Actual</p>
            <p className={`font-mono text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatCurrency(actual)}</p>
          </div>
          <div>
            <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Budget</p>
            <p className={`font-mono text-sm ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>{formatCurrency(budget)}</p>
          </div>
        </div>
        <div className={`flex items-center gap-1 mt-2 text-xs ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
          {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          <span>{formatCurrency(variance)}</span>
          <span className={isDark ? 'text-zinc-500' : 'text-slate-400'}>({variancePercent}%)</span>
        </div>
      </CardContent>
    </Card>
  );
};

const BudgetPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailRefreshing, setDetailRefreshing] = useState(false);
  const [budgets, setBudgets] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  
  // View state - 'list' or 'detail'
  const [viewMode, setViewMode] = useState('list');
  const [selectedBudgetId, setSelectedBudgetId] = useState('');
  const [budgetData, setBudgetData] = useState(null);
  
  // List view filters
  const [filterBu, setFilterBu] = useState('');
  const [filterFiscalYear, setFilterFiscalYear] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // Detail view filters
  const [timePeriod, setTimePeriod] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [hideZeroLines, setHideZeroLines] = useState(false);
  const [showVarianceColumns, setShowVarianceColumns] = useState(true); // Default ON - show Var & % columns
  
  // Expanded state
  const [expandedCategories, setExpandedCategories] = useState(new Set(['Sales', 'Purchases', 'Overhead', 'Salaries', 'Management Fees', 'Other Expenses']));
  
  // Comments panel state
  const [commentsPanelOpen, setCommentsPanelOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState({ code: '', name: '' });
  const [includeCommentsInExport, setIncludeCommentsInExport] = useState(false);
  
  // Consolidated view state
  const [showConsolidated, setShowConsolidated] = useState(false);
  const [consolidatedData, setConsolidatedData] = useState(null);
  const [loadingConsolidated, setLoadingConsolidated] = useState(false);
  const [consolidatedExpandedCategories, setConsolidatedExpandedCategories] = useState(new Set(['Sales', 'Purchases']));
  
  // List/Tile view style (for budget selection view)
  const [viewStyle, setViewStyle] = useState('list');
  
  // Financial Summary state
  const [financialSummary, setFinancialSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  
  const { isDark } = useTheme();
  const { user, isAdmin } = useAuth();

  // Sort business units alphabetically
  const sortedBusinessUnits = useMemo(() => sortAlphabetically(businessUnits, 'name'), [businessUnits]);
  
  // Get unique fiscal years sorted newest first
  const uniqueFiscalYears = useMemo(() => {
    const years = [...new Set(budgets.map(b => b.fiscal_year).filter(Boolean))];
    return sortFiscalYearsNewestFirst(years);
  }, [budgets]);

  // Check if user has BU restriction
  const userBuId = user?.business_unit_id;
  const canSeeAllBUs = isAdmin() || user?.is_cfo || !userBuId;

  const openCommentsPanel = (accountCode, accountName) => {
    setSelectedAccount({ code: accountCode, name: accountName });
    setCommentsPanelOpen(true);
  };

  useEffect(() => {
    loadData();
  }, []);

  // Set default BU filter on load
  useEffect(() => {
    if (userBuId && !canSeeAllBUs && !filterBu) {
      setFilterBu(userBuId);
    }
  }, [userBuId, canSeeAllBUs]);

  // Load financial summary when filters change
  useEffect(() => {
    loadFinancialSummary();
  }, [filterBu, filterFiscalYear]);

  const loadFinancialSummary = async () => {
    try {
      setLoadingSummary(true);
      const params = { status: 'approved' };
      if (filterFiscalYear) params.fiscal_year = filterFiscalYear;
      if (filterBu) params.business_unit_id = filterBu;
      
      const res = await budgetsApi.getFinancialSummary(params);
      setFinancialSummary(res.data);
    } catch (error) {
      console.error('Failed to load financial summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  };

  const loadData = async () => {
    try {
      setRefreshing(true);
      const [budgetsRes, busRes] = await Promise.all([
        budgetsApi.getAll(),
        settingsApi.getBusinessUnits()
      ]);
      setBudgets(budgetsRes.data);
      setBusinessUnits(busRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadBudgetDetail = async (budgetId) => {
    if (!budgetId) return;
    try {
      setDetailRefreshing(true);
      const res = await budgetsApi.getOne(budgetId);
      setBudgetData(res.data);
      setSelectedBudgetId(budgetId);
      setViewMode('detail');
    } catch (error) {
      toast.error('Failed to load budget details');
    } finally {
      setDetailRefreshing(false);
    }
  };

  const goBackToList = () => {
    setViewMode('list');
    setBudgetData(null);
    setSelectedBudgetId('');
    setShowConsolidated(false);
  };

  // Load consolidated BvA data
  const loadConsolidatedBvA = async () => {
    if (!filterFiscalYear) {
      toast.error('Please select a fiscal year first');
      return;
    }
    
    try {
      setLoadingConsolidated(true);
      const params = { fiscal_year: filterFiscalYear };
      if (filterStatus) params.status = filterStatus;
      
      const res = await budgetsApi.getConsolidatedBudgets(params);
      setConsolidatedData(res.data);
      setShowConsolidated(true);
      setViewMode('consolidated');
    } catch (error) {
      console.error('Failed to load consolidated data:', error);
      toast.error('Failed to load consolidated data');
    } finally {
      setLoadingConsolidated(false);
    }
  };

  // Filter budgets for list view
  const filteredBudgets = useMemo(() => {
    return budgets.filter(b => {
      // Always apply user BU restriction if not admin/CFO
      if (!canSeeAllBUs && userBuId && b.business_unit_id !== userBuId) return false;
      // Apply selected filter (only if user can see all BUs)
      if (canSeeAllBUs && filterBu && b.business_unit_id !== filterBu) return false;
      if (filterFiscalYear && b.fiscal_year !== filterFiscalYear) return false;
      if (filterStatus && b.status !== filterStatus) return false;
      return true;
    });
  }, [budgets, filterBu, filterFiscalYear, filterStatus]);

  // Get unique fiscal years
  const fiscalYears = useMemo(() => {
    return [...new Set(budgets.map(b => b.fiscal_year))].sort().reverse();
  }, [budgets]);

  // Get months based on time period
  const displayMonths = useMemo(() => {
    switch (timePeriod) {
      case 'q1': return ['jul', 'aug', 'sep'];
      case 'q2': return ['oct', 'nov', 'dec'];
      case 'q3': return ['jan', 'feb', 'mar'];
      case 'q4': return ['apr', 'may', 'jun'];
      case 'ytd':
        const now = new Date();
        const currentMonth = now.getMonth();
        const fiscalMonthIndex = currentMonth >= 6 ? currentMonth - 6 : currentMonth + 6;
        return MONTHS.slice(0, fiscalMonthIndex + 1);
      default:
        return selectedMonth ? [selectedMonth] : MONTHS;
    }
  }, [timePeriod, selectedMonth]);

  // Group line items by category
  const groupedItems = useMemo(() => {
    if (!budgetData?.line_items) return {};
    
    const groups = {};
    const categoryOrder = ['Sales', 'Purchases', 'Overhead', 'Salaries', 'Management Fees', 'Other Expenses'];
    
    categoryOrder.forEach(cat => {
      groups[cat] = { mainAccounts: {} };
    });
    
    budgetData.line_items.forEach((item, index) => {
      const category = item.category;
      if (!category || !groups[category]) return;
      
      const itemWithIndex = { ...item, _index: index };
      
      if (item.is_header) {
        if (!groups[category].mainAccounts[item.account_code]) {
          groups[category].mainAccounts[item.account_code] = { header: itemWithIndex, subAccounts: [] };
        } else {
          groups[category].mainAccounts[item.account_code].header = itemWithIndex;
        }
      } else {
        const parentCode = item.parent_code || 'ungrouped';
        if (!groups[category].mainAccounts[parentCode]) {
          groups[category].mainAccounts[parentCode] = { header: null, subAccounts: [] };
        }
        groups[category].mainAccounts[parentCode].subAccounts.push(itemWithIndex);
      }
    });
    
    return groups;
  }, [budgetData?.line_items]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      newSet.has(category) ? newSet.delete(category) : newSet.add(category);
      return newSet;
    });
  };

  // Calculations
  const calculateItemBudget = (item) => {
    return displayMonths.reduce((sum, m) => sum + (item[m] || 0), 0);
  };

  const calculateItemActual = (item) => {
    return displayMonths.reduce((sum, m) => {
      const actualValue = item[`${m}_actual`];
      return sum + (actualValue !== undefined && actualValue !== null ? actualValue : 0);
    }, 0);
  };

  const calculateCategoryBudget = (category) => {
    const mainAccounts = groupedItems[category]?.mainAccounts || {};
    let total = 0;
    Object.values(mainAccounts).forEach(ma => {
      ma.subAccounts.forEach(item => {
        total += calculateItemBudget(item);
      });
    });
    return total;
  };

  const calculateCategoryActual = (category) => {
    const mainAccounts = groupedItems[category]?.mainAccounts || {};
    let total = 0;
    Object.values(mainAccounts).forEach(ma => {
      ma.subAccounts.forEach(item => {
        total += calculateItemActual(item);
      });
    });
    return total;
  };

  // Totals
  // NOTE: Expense accounts (Overhead, Salaries, etc.) are stored as NEGATIVE values in the database.
  // Therefore OPEX is a negative sum. To calculate Operating Profit, we ADD opex (not subtract)
  // since subtracting a negative would incorrectly increase the profit.
  const totals = useMemo(() => {
    const sales = { budget: calculateCategoryBudget('Sales'), actual: calculateCategoryActual('Sales') };
    const purchases = { budget: calculateCategoryBudget('Purchases'), actual: calculateCategoryActual('Purchases') };
    const grossProfit = { budget: sales.budget - purchases.budget, actual: sales.actual - purchases.actual };
    const overhead = { budget: calculateCategoryBudget('Overhead'), actual: calculateCategoryActual('Overhead') };
    const salaries = { budget: calculateCategoryBudget('Salaries'), actual: calculateCategoryActual('Salaries') };
    const mgmtFees = { budget: calculateCategoryBudget('Management Fees'), actual: calculateCategoryActual('Management Fees') };
    const otherExpenses = { budget: calculateCategoryBudget('Other Expenses'), actual: calculateCategoryActual('Other Expenses') };
    const opex = { budget: overhead.budget + salaries.budget + mgmtFees.budget + otherExpenses.budget, actual: overhead.actual + salaries.actual + mgmtFees.actual + otherExpenses.actual };
    // OPEX is already negative (sum of negative expenses), so we ADD it to get the correct subtraction
    const operatingProfit = { budget: grossProfit.budget + opex.budget, actual: grossProfit.actual + opex.actual };
    const netProfit = { budget: operatingProfit.budget, actual: operatingProfit.actual };
    
    return { sales, purchases, grossProfit, overhead, salaries, mgmtFees, otherExpenses, opex, operatingProfit, netProfit };
  }, [groupedItems, displayMonths]);

  const handleExport = (format, exportType = 'full') => {
    const exportLabel = exportType === 'budget' ? 'Budget Only' : exportType === 'variance' ? 'Budget vs Actual' : 'Full Report';
    const withComments = includeCommentsInExport ? ' (with comments)' : '';
    toast.info(`Exporting ${exportLabel}${withComments} as ${format.toUpperCase()}...`);
    setTimeout(() => toast.success('Export ready for download'), 1000);
  };

  const filterZeroLines = (subAccounts) => {
    if (!hideZeroLines) return subAccounts;
    return subAccounts.filter(item => {
      const hasBudget = displayMonths.some(m => (item[m] || 0) !== 0);
      const hasActual = displayMonths.some(m => {
        const actualValue = item[`${m}_actual`];
        return actualValue !== undefined && actualValue !== null && actualValue !== 0;
      });
      return hasBudget || hasActual;
    });
  };

  // Calculate category budget for a specific month
  const calculateCategoryMonthBudget = (category, month) => {
    const mainAccounts = groupedItems[category]?.mainAccounts || {};
    let total = 0;
    Object.values(mainAccounts).forEach(ma => {
      ma.subAccounts.forEach(item => {
        total += item[month] || 0;
      });
    });
    return total;
  };

  // Calculate category actual for a specific month
  const calculateCategoryMonthActual = (category, month) => {
    const mainAccounts = groupedItems[category]?.mainAccounts || {};
    let total = 0;
    Object.values(mainAccounts).forEach(ma => {
      ma.subAccounts.forEach(item => {
        const actualValue = item[`${month}_actual`];
        total += actualValue !== undefined && actualValue !== null ? actualValue : 0;
      });
    });
    return total;
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="budget-vs-actual-loading">
        <Skeleton className="h-10 w-64 rounded-sm" />
        <Skeleton className="h-96 rounded-sm" />
      </div>
    );
  }

  // ==================== LIST VIEW ====================
  if (viewMode === 'list') {
    return (
      <div className={`p-6 space-y-6 ${isDark ? 'text-white' : ''}`} data-testid="budget-vs-actual-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`font-display text-3xl font-light ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Budget vs Actuals
            </h1>
            <p className={isDark ? 'text-zinc-400 mt-1' : 'text-slate-500 mt-1'}>
              Select a budget to compare against actual performance
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className={`p-4 rounded-sm ${isDark ? 'bg-zinc-800/50 border border-zinc-700' : 'bg-slate-50 border border-slate-200'}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <Select 
              value={filterBu || 'all'} 
              onValueChange={(v) => setFilterBu(v === 'all' ? '' : v)}
              disabled={!canSeeAllBUs}
            >
              <SelectTrigger className={`w-[180px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white'}`}>
                <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Business Unit" />
              </SelectTrigger>
              <SelectContent>
                {canSeeAllBUs && <SelectItem value="all">All Business Units</SelectItem>}
                {sortedBusinessUnits.map(bu => (
                  <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterFiscalYear || 'all'} onValueChange={(v) => setFilterFiscalYear(v === 'all' ? '' : v)}>
              <SelectTrigger className={`w-[150px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white'}`}>
                <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Fiscal Year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {uniqueFiscalYears.map(fy => (
                  <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
              <SelectTrigger className={`w-[140px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white'}`}>
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={loadData} disabled={refreshing} className={`rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white'}`}>
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>

            {/* List/Tile View Toggle */}
            <div className={`flex items-center rounded-sm overflow-hidden border ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => setViewStyle('list')}
                className={`rounded-none px-3 h-9 ${viewStyle === 'list' 
                  ? isDark ? 'bg-zinc-700 text-white' : 'bg-slate-200 text-slate-900'
                  : isDark ? 'text-zinc-400' : 'text-slate-500'
                }`}
                data-testid="bva-view-list-btn"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => setViewStyle('tile')}
                className={`rounded-none px-3 h-9 border-l ${isDark ? 'border-zinc-700' : 'border-slate-200'} ${viewStyle === 'tile' 
                  ? isDark ? 'bg-zinc-700 text-white' : 'bg-slate-200 text-slate-900'
                  : isDark ? 'text-zinc-400' : 'text-slate-500'
                }`}
                data-testid="bva-view-tile-btn"
              >
                <Grid className="w-4 h-4" />
              </Button>
            </div>

            {/* Consolidated View Button - Only for Admin/CFO */}
            {canSeeAllBUs && (
              <Button 
                variant="default"
                onClick={() => {
                  if (!filterFiscalYear) {
                    toast.error('Please select a Fiscal Year first for consolidated view');
                    return;
                  }
                  loadConsolidatedBvA();
                }} 
                disabled={loadingConsolidated}
                className="rounded-sm bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="consolidated-bva-btn"
              >
                <LayoutGrid className="w-4 h-4 mr-2" />
                {loadingConsolidated ? 'Loading...' : 'Consolidated BvA'}
              </Button>
            )}
          </div>
        </div>

        {/* Budget List - List or Tile View */}
        {viewStyle === 'list' ? (
          /* LIST VIEW */
          <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
            <CardHeader className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
              <CardTitle className="font-display text-lg font-medium">
                Select a Budget ({filteredBudgets.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {filteredBudgets.length === 0 ? (
                <div className={`p-8 text-center ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  <FileSpreadsheet className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-zinc-600' : 'text-slate-300'}`} />
                  <p>No budgets found matching your filters</p>
                </div>
              ) : (
                <div className={`divide-y ${isDark ? 'divide-zinc-800' : 'divide-slate-100'}`}>
                  {filteredBudgets.map(budget => {
                    const bu = businessUnits.find(b => b.id === budget.business_unit_id);
                    return (
                      <div 
                        key={budget.id}
                        className={`p-4 flex items-center justify-between cursor-pointer ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-50'}`}
                        onClick={() => loadBudgetDetail(budget.id)}
                        data-testid={`budget-list-${budget.id}`}
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                            isDark ? 'bg-blue-900/30' : 'bg-blue-50'
                          }`}>
                            <BarChart3 className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                              <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                {budget.name}
                              </p>
                              <Badge variant={budget.status === 'approved' ? 'default' : 'outline'} className={`text-xs
                                ${budget.status === 'approved' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}
                                ${budget.status === 'pending' ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' : ''}
                              `}>
                                {budget.status}
                              </Badge>
                            </div>
                            <div className={`flex items-center gap-4 text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {bu?.name || 'Unknown BU'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                FY {budget.fiscal_year}
                              </span>
                            </div>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="rounded-sm gap-1">
                          <Eye className="w-4 h-4" />
                          View Analysis
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* TILE VIEW */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredBudgets.length === 0 ? (
              <Card className={`col-span-full border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <FileSpreadsheet className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-zinc-600' : 'text-slate-300'}`} />
                  <p className={isDark ? 'text-zinc-400' : 'text-slate-500'}>No budgets found matching your filters</p>
                </CardContent>
              </Card>
            ) : (
              filteredBudgets.map(budget => {
                const bu = businessUnits.find(b => b.id === budget.business_unit_id);
                return (
                  <Card 
                    key={budget.id} 
                    className={`border shadow-none rounded-sm cursor-pointer transition-all hover:shadow-md ${isDark ? 'bg-zinc-900 border-zinc-800 hover:border-zinc-600' : 'border-slate-200 hover:border-slate-400'}`}
                    onClick={() => loadBudgetDetail(budget.id)}
                    data-testid={`budget-card-${budget.id}`}
                  >
                    <CardHeader className={`pb-2 border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className={`font-display text-base font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {budget.name}
                          </CardTitle>
                          <p className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                            {bu?.name || 'Unknown BU'}
                          </p>
                        </div>
                        <Badge variant={budget.status === 'approved' ? 'default' : 'outline'} className={`
                          ${budget.status === 'approved' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}
                          ${budget.status === 'pending' ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' : ''}
                          ${budget.status === 'draft' ? isDark ? 'border-zinc-600 text-zinc-400' : '' : ''}
                        `}>
                          {budget.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className={`w-4 h-4 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`} />
                          <span className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                            {budget.fiscal_year}
                          </span>
                        </div>
                        <Button variant="ghost" size="sm" className="rounded-sm gap-1">
                          <Eye className="w-4 h-4" />
                          View Analysis
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}
      </div>
    );
  }

  // ==================== CONSOLIDATED VIEW ====================
  if (viewMode === 'consolidated' && consolidatedData) {
    // Calculate consolidated totals for summary cards - both total and by month
    const consolidatedTotals = {
      sales: { budget: 0, actual: 0 },
      purchases: { budget: 0, actual: 0 },
      overhead: { budget: 0, actual: 0 },
      salaries: { budget: 0, actual: 0 },
      mgmtFees: { budget: 0, actual: 0 },
      otherExpenses: { budget: 0, actual: 0 },
    };

    // Initialize monthly totals for each category
    const consolidatedMonthlyTotals = {};
    for (const month of MONTHS) {
      consolidatedMonthlyTotals[month] = {
        sales: { budget: 0, actual: 0 },
        purchases: { budget: 0, actual: 0 },
        overhead: { budget: 0, actual: 0 },
        salaries: { budget: 0, actual: 0 },
        mgmtFees: { budget: 0, actual: 0 },
        otherExpenses: { budget: 0, actual: 0 },
      };
    }

    // Group items by category for collapsible view
    const consolidatedGroupedItems = {};
    
    for (const item of (consolidatedData.line_items || [])) {
      const category = item.category || 'Other Expenses';
      if (!consolidatedGroupedItems[category]) {
        consolidatedGroupedItems[category] = {
          items: [],
          totalBudget: 0,
          totalActual: 0
        };
      }
      
      // Calculate item totals and monthly values
      let itemBudget = 0;
      let itemActual = 0;
      for (const month of MONTHS) {
        const budgetVal = item[month] || 0;
        const actualValue = item[`${month}_actual`];
        const actualVal = actualValue !== undefined && actualValue !== null ? actualValue : 0;
        itemBudget += budgetVal;
        itemActual += actualVal;

        // Accumulate monthly totals by category
        const catKey = category === 'Sales' ? 'sales' :
                       category === 'Purchases' ? 'purchases' :
                       category === 'Overhead' ? 'overhead' :
                       category === 'Salaries' ? 'salaries' :
                       category === 'Management Fees' ? 'mgmtFees' : 'otherExpenses';
        consolidatedMonthlyTotals[month][catKey].budget += budgetVal;
        consolidatedMonthlyTotals[month][catKey].actual += actualVal;
      }
      
      // Skip zero items if hideZeroLines is enabled
      if (hideZeroLines && itemBudget === 0 && itemActual === 0) continue;
      
      consolidatedGroupedItems[category].items.push({ ...item, itemBudget, itemActual });
      consolidatedGroupedItems[category].totalBudget += itemBudget;
      consolidatedGroupedItems[category].totalActual += itemActual;
      
      // Accumulate totals for summary cards
      if (category === 'Sales') {
        consolidatedTotals.sales.budget += itemBudget;
        consolidatedTotals.sales.actual += itemActual;
      } else if (category === 'Purchases') {
        consolidatedTotals.purchases.budget += itemBudget;
        consolidatedTotals.purchases.actual += itemActual;
      } else if (category === 'Overhead') {
        consolidatedTotals.overhead.budget += itemBudget;
        consolidatedTotals.overhead.actual += itemActual;
      } else if (category === 'Salaries') {
        consolidatedTotals.salaries.budget += itemBudget;
        consolidatedTotals.salaries.actual += itemActual;
      } else if (category === 'Management Fees') {
        consolidatedTotals.mgmtFees.budget += itemBudget;
        consolidatedTotals.mgmtFees.actual += itemActual;
      } else {
        consolidatedTotals.otherExpenses.budget += itemBudget;
        consolidatedTotals.otherExpenses.actual += itemActual;
      }
    }

    // Calculate derived totals
    // NOTE: Expense accounts are stored as NEGATIVE values. OPEX is a negative sum.
    // To calculate Operating Profit, we ADD opex (not subtract) since subtracting a negative would incorrectly increase profit.
    const grossProfitBudget = consolidatedTotals.sales.budget - consolidatedTotals.purchases.budget;
    const grossProfitActual = consolidatedTotals.sales.actual - consolidatedTotals.purchases.actual;
    const opexBudget = consolidatedTotals.overhead.budget + consolidatedTotals.salaries.budget + consolidatedTotals.mgmtFees.budget + consolidatedTotals.otherExpenses.budget;
    const opexActual = consolidatedTotals.overhead.actual + consolidatedTotals.salaries.actual + consolidatedTotals.mgmtFees.actual + consolidatedTotals.otherExpenses.actual;
    // OPEX is already negative, so we ADD it to get the correct subtraction
    const operatingProfitBudget = grossProfitBudget + opexBudget;
    const operatingProfitActual = grossProfitActual + opexActual;
    const netProfitBudget = operatingProfitBudget;
    const netProfitActual = operatingProfitActual;

    const consolidatedSummaryTotals = {
      sales: { budget: consolidatedTotals.sales.budget, actual: consolidatedTotals.sales.actual },
      purchases: { budget: consolidatedTotals.purchases.budget, actual: consolidatedTotals.purchases.actual },
      grossProfit: { budget: grossProfitBudget, actual: grossProfitActual },
      opex: { budget: opexBudget, actual: opexActual },
      operatingProfit: { budget: operatingProfitBudget, actual: operatingProfitActual },
      netProfit: { budget: netProfitBudget, actual: netProfitActual },
    };

    // Helper function to get monthly derived values
    const getMonthlyDerived = (month) => {
      const m = consolidatedMonthlyTotals[month];
      const salesBud = m.sales.budget;
      const salesAct = m.sales.actual;
      const purchasesBud = m.purchases.budget;
      const purchasesAct = m.purchases.actual;
      const gpBud = salesBud - purchasesBud;
      const gpAct = salesAct - purchasesAct;
      const opexBud = m.overhead.budget + m.salaries.budget + m.mgmtFees.budget + m.otherExpenses.budget;
      const opexAct = m.overhead.actual + m.salaries.actual + m.mgmtFees.actual + m.otherExpenses.actual;
      // OPEX is already negative, so ADD it
      const npBud = gpBud + opexBud;
      const npAct = gpAct + opexAct;
      return {
        sales: { budget: salesBud, actual: salesAct },
        purchases: { budget: purchasesBud, actual: purchasesAct },
        grossProfit: { budget: gpBud, actual: gpAct },
        opex: { budget: opexBud, actual: opexAct },
        netProfit: { budget: npBud, actual: npAct },
      };
    };

    // Toggle function for consolidated categories
    const toggleConsolidatedCategory = (category) => {
      setConsolidatedExpandedCategories(prev => {
        const next = new Set(prev);
        if (next.has(category)) next.delete(category);
        else next.add(category);
        return next;
      });
    };

    return (
      <div className={`p-6 space-y-6 ${isDark ? 'text-white' : ''}`} data-testid="consolidated-bva-view">
        {/* Header: Back | Title | Filters (left to right) */}
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            {/* Back Button */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={goBackToList} 
              className={`rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
              data-testid="back-to-list-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            {/* Title */}
            <div>
              <h1 className={`font-display text-2xl font-light flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <LayoutGrid className="w-6 h-6 text-purple-500" />
                Consolidated Budget vs Actuals
              </h1>
              <p className={isDark ? 'text-zinc-400 mt-0.5' : 'text-slate-500 mt-0.5'}>
                {consolidatedData.budget_count} budget(s) • 
                <span className="ml-1">{consolidatedData.business_units_included?.map(bu => bu.name).join(', ')}</span>
              </p>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 ml-4">
              {/* Fiscal Year Selector */}
              <Select value={filterFiscalYear} onValueChange={(fy) => {
                setFilterFiscalYear(fy);
                // Reload consolidated data with new fiscal year
                setTimeout(() => loadConsolidatedBvA(), 100);
              }}>
                <SelectTrigger className={`w-40 rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white'}`} data-testid="consolidated-fy-select">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Fiscal Year" />
                </SelectTrigger>
                <SelectContent>
                  {uniqueFiscalYears.map(fy => (
                    <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Hide Zeros Toggle */}
              <div className={`flex items-center gap-2 px-3 py-2 rounded-sm border ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-slate-200'}`}>
                <Checkbox 
                  id="hide-zeros-consolidated" 
                  checked={hideZeroLines} 
                  onCheckedChange={setHideZeroLines}
                />
                <Label htmlFor="hide-zeros-consolidated" className={`text-sm cursor-pointer ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
                  <EyeOff className="w-3 h-3 inline mr-1" />
                  Hide Zeros
                </Label>
              </div>

              {/* Show Var & % Toggle */}
              <div className={`flex items-center gap-2 px-3 py-2 rounded-sm border ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-slate-200'}`}>
                <Checkbox 
                  id="show-variance-consolidated" 
                  checked={showVarianceColumns} 
                  onCheckedChange={setShowVarianceColumns}
                />
                <Label htmlFor="show-variance-consolidated" className={`text-sm cursor-pointer ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
                  <BarChart3 className="w-3 h-3 inline mr-1" />
                  Show Var & %
                </Label>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Info Bar */}
        <div className={`p-3 rounded-sm flex items-center gap-4 flex-wrap ${isDark ? 'bg-purple-900/20 border border-purple-800/50' : 'bg-purple-50 border border-purple-200'}`}>
          <LayoutGrid className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
          <span className={`font-medium ${isDark ? 'text-purple-400' : 'text-purple-700'}`}>Consolidated Performance</span>
          <Badge variant="outline" className={isDark ? 'border-purple-700 text-purple-400' : 'border-purple-300 text-purple-700'}>
            FY {consolidatedData.fiscal_year}
          </Badge>
          <span className={`text-sm ${isDark ? 'text-purple-300' : 'text-purple-600'}`}>
            All Business Units Combined
          </span>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <SummaryCard label="Sales" budget={consolidatedSummaryTotals.sales.budget} actual={consolidatedSummaryTotals.sales.actual} isDark={isDark} />
          <SummaryCard label="Purchases" budget={consolidatedSummaryTotals.purchases.budget} actual={consolidatedSummaryTotals.purchases.actual} isDark={isDark} isExpense />
          <SummaryCard label="Gross Profit" budget={consolidatedSummaryTotals.grossProfit.budget} actual={consolidatedSummaryTotals.grossProfit.actual} isDark={isDark} highlight />
          <SummaryCard label="Opex" budget={consolidatedSummaryTotals.opex.budget} actual={consolidatedSummaryTotals.opex.actual} isDark={isDark} isExpense />
          <SummaryCard label="Operating Profit" budget={consolidatedSummaryTotals.operatingProfit.budget} actual={consolidatedSummaryTotals.operatingProfit.actual} isDark={isDark} />
          <SummaryCard label="Net Profit" budget={consolidatedSummaryTotals.netProfit.budget} actual={consolidatedSummaryTotals.netProfit.actual} isDark={isDark} highlight />
        </div>

        {/* Category Tables - Collapsible */}
        <div className="space-y-4">
          {Object.entries(consolidatedGroupedItems).map(([category, data]) => {
            if (data.items.length === 0) return null;
            
            const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS['Other Expenses'];
            const isExpanded = consolidatedExpandedCategories.has(category);
            const categoryVariance = data.totalActual - data.totalBudget;
            
            return (
              <Card key={category} className={`border shadow-none rounded-sm overflow-hidden ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                <Collapsible open={isExpanded} onOpenChange={() => toggleConsolidatedCategory(category)}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className={`py-3 px-4 ${colors.bg} ${colors.border} border-b cursor-pointer hover:opacity-90`}>
                      <div className="flex items-center justify-between">
                        <CardTitle className={`font-display text-base font-medium flex items-center gap-2 ${colors.text}`}>
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <Layers className="w-4 h-4" />
                          {category}
                          <Badge variant="outline" className={`ml-2 text-xs ${colors.text}`}>
                            {data.items.length} accounts
                          </Badge>
                        </CardTitle>
                        <div className="flex items-center gap-6 font-mono text-sm">
                          <div className="text-right">
                            <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Actual</span>
                            <p className={colors.text}>{formatCurrency(data.totalActual)}</p>
                          </div>
                          <div className="text-right">
                            <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Budget</span>
                            <p className={colors.text}>{formatCurrency(data.totalBudget)}</p>
                          </div>
                          <div className="text-right">
                            <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Variance</span>
                            <p className={categoryVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                              {categoryVariance >= 0 ? '+' : ''}{formatCurrency(categoryVariance)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="p-0 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}>
                            <th className="text-left py-2 px-4 font-medium w-16">Code</th>
                            <th className="text-left py-2 px-4 font-medium min-w-[200px]">Account</th>
                            {MONTHS.map(month => (
                              <th key={month} colSpan={showVarianceColumns ? 4 : 2} className="text-center py-2 px-2 font-medium border-l border-slate-200 dark:border-zinc-700">
                                {MONTH_NAMES[month].substring(0, 3)}
                              </th>
                            ))}
                            <th className="text-right py-2 px-3 font-medium border-l border-slate-200 dark:border-zinc-700">Total Actual</th>
                            <th className="text-right py-2 px-3 font-medium">Total Budget</th>
                            <th className="text-right py-2 px-3 font-medium">Variance</th>
                          </tr>
                          <tr className={`text-xs ${isDark ? 'bg-zinc-800/30 text-zinc-500' : 'bg-slate-50/50 text-slate-400'}`}>
                            <th></th>
                            <th></th>
                            {MONTHS.map(month => (
                              <React.Fragment key={month}>
                                <th className="py-1 px-1 text-center border-l border-slate-200 dark:border-zinc-700">Act</th>
                                <th className="py-1 px-1 text-center">Bud</th>
                                {showVarianceColumns && (
                                  <>
                                    <th className="py-1 px-1 text-center">Var</th>
                                    <th className="py-1 px-1 text-center">%</th>
                                  </>
                                )}
                              </React.Fragment>
                            ))}
                            <th className="border-l border-slate-200 dark:border-zinc-700"></th>
                            <th></th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.items.map((item, idx) => {
                            const variance = item.itemActual - item.itemBudget;
                            
                            return (
                              <tr key={idx} className={`border-t ${isDark ? 'border-zinc-800 hover:bg-zinc-800/30' : 'border-slate-100 hover:bg-slate-50'}`}>
                                <td className={`py-2 px-4 font-mono text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                                  {item.account_code}
                                </td>
                                <td className={`py-2 px-4 ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                                  {item.account_name}
                                </td>
                                {MONTHS.map(month => {
                                  const budgetVal = item[month] || 0;
                                  const actualValue = item[`${month}_actual`];
                                  const actualVal = actualValue !== undefined && actualValue !== null ? actualValue : 0;
                                  const monthVariance = actualVal - budgetVal;
                                  const monthPct = budgetVal !== 0 ? (actualVal / budgetVal * 100) : 0;
                                  return (
                                    <React.Fragment key={month}>
                                      <td className={`py-2 px-1 text-right font-mono text-xs border-l ${isDark ? 'border-zinc-700 text-zinc-300' : 'border-slate-200 text-slate-700'}`}>
                                        {actualVal !== 0 ? formatCurrency(actualVal) : '-'}
                                      </td>
                                      <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                                        {budgetVal !== 0 ? formatCurrency(budgetVal) : '-'}
                                      </td>
                                      {showVarianceColumns && (
                                        <>
                                          <td className={`py-2 px-1 text-right font-mono text-xs ${monthVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                            {budgetVal !== 0 || actualVal !== 0 ? formatCurrency(monthVariance) : '-'}
                                          </td>
                                          <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                                            {budgetVal !== 0 ? `${monthPct.toFixed(1)}%` : '-'}
                                          </td>
                                        </>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                                <td className={`py-2 px-3 text-right font-mono font-medium border-l ${isDark ? 'border-zinc-700 text-white' : 'border-slate-200 text-slate-900'}`}>
                                  {formatCurrency(item.itemActual)}
                                </td>
                                <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                                  {formatCurrency(item.itemBudget)}
                                </td>
                                <td className={`py-2 px-3 text-right font-mono font-medium ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                  {variance >= 0 ? '+' : ''}{formatCurrency(variance)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>

        {/* Financial Summary - Bottom of Consolidated BvA View - WITH MONTHLY BREAKDOWN */}
        <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
          <CardHeader className={`py-3 border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
            <CardTitle className="font-display text-base font-medium flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-600" />
              Financial Summary by Month
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className={isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}>
                    <th className={`py-2 px-4 text-left font-medium sticky left-0 z-10 ${isDark ? 'text-zinc-300 bg-zinc-800' : 'text-slate-700 bg-slate-50'}`}>Category</th>
                    {MONTHS.map(month => (
                      <th key={month} colSpan={2} className={`py-2 px-1 text-center font-medium border-l ${isDark ? 'text-zinc-300 border-zinc-700' : 'text-slate-700 border-slate-200'}`}>
                        {MONTH_LABELS[month]}
                      </th>
                    ))}
                    <th colSpan={2} className={`py-2 px-3 text-center font-medium border-l-2 ${isDark ? 'text-white border-zinc-600 bg-zinc-700' : 'text-slate-900 border-slate-300 bg-slate-100'}`}>
                      YTD Total
                    </th>
                    <th className={`py-2 px-3 text-center font-medium border-l ${isDark ? 'text-zinc-300 border-zinc-700' : 'text-slate-700 border-slate-200'}`}>Variance</th>
                    <th className={`py-2 px-3 text-center font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>%</th>
                  </tr>
                  <tr className={isDark ? 'bg-zinc-800/30' : 'bg-slate-50/50'}>
                    <th className={`py-1 px-4 sticky left-0 z-10 ${isDark ? 'bg-zinc-800' : 'bg-slate-50'}`}></th>
                    {MONTHS.map(month => (
                      <React.Fragment key={month}>
                        <th className={`py-1 px-1 text-center text-xs font-normal border-l ${isDark ? 'text-zinc-400 border-zinc-700' : 'text-slate-500 border-slate-200'}`}>Act</th>
                        <th className={`py-1 px-1 text-center text-xs font-normal ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Bud</th>
                      </React.Fragment>
                    ))}
                    <th className={`py-1 px-1 text-center text-xs font-normal border-l-2 ${isDark ? 'text-zinc-400 border-zinc-600' : 'text-slate-500 border-slate-300'}`}>Act</th>
                    <th className={`py-1 px-1 text-center text-xs font-normal ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Bud</th>
                    <th className={`py-1 px-1 text-center text-xs font-normal border-l ${isDark ? 'text-zinc-400 border-zinc-700' : 'text-slate-500 border-slate-200'}`}></th>
                    <th className={`py-1 px-1 text-center text-xs font-normal ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Sales Row */}
                  <tr className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                    <td className={`py-2 px-4 font-medium sticky left-0 z-10 ${isDark ? 'text-zinc-300 bg-zinc-900' : 'text-slate-700 bg-white'}`}>Sales</td>
                    {MONTHS.map(month => {
                      const m = getMonthlyDerived(month);
                      return (
                        <React.Fragment key={month}>
                          <td className={`py-2 px-1 text-right font-mono text-xs border-l ${isDark ? 'text-white border-zinc-700' : 'text-slate-900 border-slate-200'}`}>
                            {formatCurrency(m.sales.actual)}
                          </td>
                          <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                            {formatCurrency(m.sales.budget)}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    <td className={`py-2 px-3 text-right font-mono font-medium border-l-2 ${isDark ? 'text-white border-zinc-600' : 'text-slate-900 border-slate-300'}`}>{formatCurrency(consolidatedSummaryTotals.sales.actual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(consolidatedSummaryTotals.sales.budget)}</td>
                    <td className={`py-2 px-3 text-right font-mono border-l ${consolidatedSummaryTotals.sales.actual - consolidatedSummaryTotals.sales.budget >= 0 ? 'text-emerald-600' : 'text-red-600'} ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                      {consolidatedSummaryTotals.sales.actual - consolidatedSummaryTotals.sales.budget >= 0 ? '+' : ''}{formatCurrency(consolidatedSummaryTotals.sales.actual - consolidatedSummaryTotals.sales.budget)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                      {consolidatedSummaryTotals.sales.budget !== 0 ? `${(consolidatedSummaryTotals.sales.actual / consolidatedSummaryTotals.sales.budget * 100).toFixed(1)}%` : '-'}
                    </td>
                  </tr>

                  {/* Purchases Row */}
                  <tr className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                    <td className={`py-2 px-4 font-medium sticky left-0 z-10 ${isDark ? 'text-zinc-300 bg-zinc-900' : 'text-slate-700 bg-white'}`}>Less: Purchases</td>
                    {MONTHS.map(month => {
                      const m = getMonthlyDerived(month);
                      return (
                        <React.Fragment key={month}>
                          <td className={`py-2 px-1 text-right font-mono text-xs border-l text-red-600 ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                            {formatCurrencyAsExpense(m.purchases.actual)}
                          </td>
                          <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                            {formatCurrencyAsExpense(m.purchases.budget)}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    <td className={`py-2 px-3 text-right font-mono font-medium border-l-2 text-red-600 ${isDark ? 'border-zinc-600' : 'border-slate-300'}`}>{formatCurrencyAsExpense(consolidatedSummaryTotals.purchases.actual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrencyAsExpense(consolidatedSummaryTotals.purchases.budget)}</td>
                    <td className={`py-2 px-3 text-right font-mono border-l ${consolidatedSummaryTotals.purchases.budget - consolidatedSummaryTotals.purchases.actual >= 0 ? 'text-emerald-600' : 'text-red-600'} ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                      {consolidatedSummaryTotals.purchases.budget - consolidatedSummaryTotals.purchases.actual >= 0 ? '+' : ''}{formatCurrency(consolidatedSummaryTotals.purchases.budget - consolidatedSummaryTotals.purchases.actual)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                      {consolidatedSummaryTotals.purchases.budget !== 0 ? `${(consolidatedSummaryTotals.purchases.actual / consolidatedSummaryTotals.purchases.budget * 100).toFixed(1)}%` : '-'}
                    </td>
                  </tr>

                  {/* Gross Profit Row */}
                  <tr className={`border-b-2 ${isDark ? 'border-zinc-700 bg-emerald-900/10' : 'border-slate-200 bg-emerald-50'}`}>
                    <td className={`py-2 px-4 font-semibold sticky left-0 z-10 ${isDark ? 'text-emerald-400 bg-emerald-900/20' : 'text-emerald-700 bg-emerald-50'}`}>Gross Profit</td>
                    {MONTHS.map(month => {
                      const m = getMonthlyDerived(month);
                      return (
                        <React.Fragment key={month}>
                          <td className={`py-2 px-1 text-right font-mono text-xs font-medium border-l ${m.grossProfit.actual >= 0 ? 'text-emerald-600' : 'text-red-600'} ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                            {formatCurrency(m.grossProfit.actual)}
                          </td>
                          <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                            {formatCurrency(m.grossProfit.budget)}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    <td className={`py-2 px-3 text-right font-mono font-bold border-l-2 ${consolidatedSummaryTotals.grossProfit.actual >= 0 ? 'text-emerald-600' : 'text-red-600'} ${isDark ? 'border-zinc-600' : 'border-slate-300'}`}>{formatCurrency(consolidatedSummaryTotals.grossProfit.actual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(consolidatedSummaryTotals.grossProfit.budget)}</td>
                    <td className={`py-2 px-3 text-right font-mono font-medium border-l ${consolidatedSummaryTotals.grossProfit.actual - consolidatedSummaryTotals.grossProfit.budget >= 0 ? 'text-emerald-600' : 'text-red-600'} ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                      {consolidatedSummaryTotals.grossProfit.actual - consolidatedSummaryTotals.grossProfit.budget >= 0 ? '+' : ''}{formatCurrency(consolidatedSummaryTotals.grossProfit.actual - consolidatedSummaryTotals.grossProfit.budget)}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                      {consolidatedSummaryTotals.grossProfit.budget !== 0 ? `${(consolidatedSummaryTotals.grossProfit.actual / consolidatedSummaryTotals.grossProfit.budget * 100).toFixed(1)}%` : '-'}
                    </td>
                  </tr>

                  {/* Total Opex Row */}
                  <tr className={`border-b ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                    <td className={`py-2 px-4 font-medium sticky left-0 z-10 ${isDark ? 'text-zinc-300 bg-zinc-900' : 'text-slate-700 bg-white'}`}>Total Opex</td>
                    {MONTHS.map(month => {
                      const m = getMonthlyDerived(month);
                      return (
                        <React.Fragment key={month}>
                          <td className={`py-2 px-1 text-right font-mono text-xs border-l ${isDark ? 'text-white border-zinc-700' : 'text-slate-900 border-slate-200'}`}>
                            {formatCurrencyAsExpense(m.opex.actual)}
                          </td>
                          <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                            {formatCurrencyAsExpense(m.opex.budget)}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    <td className={`py-2 px-3 text-right font-mono font-medium border-l-2 ${isDark ? 'text-white border-zinc-600' : 'text-slate-900 border-slate-300'}`}>{formatCurrencyAsExpense(consolidatedSummaryTotals.opex.actual)}</td>
                    <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrencyAsExpense(consolidatedSummaryTotals.opex.budget)}</td>
                    <td className={`py-2 px-3 text-right font-mono border-l ${consolidatedSummaryTotals.opex.budget - consolidatedSummaryTotals.opex.actual >= 0 ? 'text-emerald-600' : 'text-red-600'} ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                      {consolidatedSummaryTotals.opex.budget - consolidatedSummaryTotals.opex.actual >= 0 ? '+' : ''}{formatCurrency(consolidatedSummaryTotals.opex.budget - consolidatedSummaryTotals.opex.actual)}
                    </td>
                  </tr>

                  {/* Net Profit Row */}
                  <tr className={`${isDark ? 'bg-emerald-900/20' : 'bg-emerald-50'}`}>
                    <td className={`py-3 px-4 font-bold sticky left-0 z-10 ${isDark ? 'text-emerald-400 bg-emerald-900/30' : 'text-emerald-700 bg-emerald-100'}`}>Net Profit / (Loss)</td>
                    {MONTHS.map(month => {
                      const m = getMonthlyDerived(month);
                      return (
                        <React.Fragment key={month}>
                          <td className={`py-3 px-1 text-right font-mono text-xs font-medium border-l ${m.netProfit.actual >= 0 ? 'text-emerald-600' : 'text-red-600'} ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                            {formatCurrency(m.netProfit.actual)}
                          </td>
                          <td className={`py-3 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                            {formatCurrency(m.netProfit.budget)}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    <td className={`py-3 px-3 text-right font-mono font-bold border-l-2 ${consolidatedSummaryTotals.netProfit.actual >= 0 ? 'text-emerald-600' : 'text-red-600'} ${isDark ? 'border-zinc-600' : 'border-slate-300'}`}>{formatCurrency(consolidatedSummaryTotals.netProfit.actual)}</td>
                    <td className={`py-3 px-3 text-right font-mono font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(consolidatedSummaryTotals.netProfit.budget)}</td>
                    <td className={`py-3 px-3 text-right font-mono font-bold border-l ${consolidatedSummaryTotals.netProfit.actual - consolidatedSummaryTotals.netProfit.budget >= 0 ? 'text-emerald-600' : 'text-red-600'} ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                      {consolidatedSummaryTotals.netProfit.actual - consolidatedSummaryTotals.netProfit.budget >= 0 ? '+' : ''}{formatCurrency(consolidatedSummaryTotals.netProfit.actual - consolidatedSummaryTotals.netProfit.budget)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ==================== DETAIL VIEW ====================
  return (
    <div className={`p-6 space-y-6 ${isDark ? 'text-white' : ''}`} data-testid="budget-vs-actual-detail">
      {/* Header with Back Button */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={goBackToList} 
              className={`rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
              data-testid="back-to-list-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className={`font-display text-2xl font-light ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Budget vs Actuals
              </h1>
              <p className={isDark ? 'text-zinc-400 mt-0.5' : 'text-slate-500 mt-0.5'}>
                Variance analysis for {budgetData?.name}
              </p>
            </div>
          </div>
        </div>
        
        {/* Filters Row */}
        <div className={`p-3 rounded-sm ${isDark ? 'bg-zinc-800/50 border border-zinc-700' : 'bg-slate-50 border border-slate-200'}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={timePeriod} onValueChange={setTimePeriod}>
              <SelectTrigger className={`w-[130px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white'}`}>
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIME_PERIODS.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedMonth || 'all'} onValueChange={(v) => setSelectedMonth(v === 'all' ? '' : v)}>
              <SelectTrigger className={`w-[120px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white'}`}>
                <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {MONTHS.map(m => (
                  <SelectItem key={m} value={m}>{MONTH_NAMES[m]}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className={`flex items-center gap-2 px-3 py-2 rounded-sm border ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-slate-200'}`}>
              <Checkbox 
                id="hide-zero" 
                checked={hideZeroLines} 
                onCheckedChange={setHideZeroLines}
                data-testid="hide-zero-checkbox"
              />
              <Label htmlFor="hide-zero" className={`text-sm cursor-pointer whitespace-nowrap ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
                <EyeOff className="w-3 h-3 inline mr-1" />
                Hide zeros
              </Label>
            </div>

            <div className={`flex items-center gap-2 px-3 py-2 rounded-sm border ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-slate-200'}`}>
              <Checkbox 
                id="show-variance" 
                checked={showVarianceColumns} 
                onCheckedChange={setShowVarianceColumns}
                data-testid="show-variance-checkbox"
              />
              <Label htmlFor="show-variance" className={`text-sm cursor-pointer whitespace-nowrap ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
                <BarChart3 className="w-3 h-3 inline mr-1" />
                Show Var & %
              </Label>
            </div>

            <Button variant="outline" size="icon" onClick={() => loadBudgetDetail(selectedBudgetId)} disabled={detailRefreshing} className={`rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white'}`}>
              <RefreshCw className={`w-4 h-4 ${detailRefreshing ? 'animate-spin' : ''}`} />
            </Button>

            {/* Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={`rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white'}`} data-testid="export-dropdown">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className={`px-2 py-1.5 text-xs font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  Export Data
                </div>
                <DropdownMenuItem onClick={() => handleExport('xlsx', 'budget')} data-testid="export-budget-only">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Budget Only
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('xlsx', 'variance')}>
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Budget vs Actual
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <div className={`px-2 py-1.5 flex items-center gap-2`}>
                  <Checkbox 
                    id="include-comments-export" 
                    checked={includeCommentsInExport} 
                    onCheckedChange={setIncludeCommentsInExport}
                  />
                  <Label htmlFor="include-comments-export" className={`text-xs cursor-pointer ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
                    <MessageSquare className="w-3 h-3 inline mr-1" />
                    Include Comments
                  </Label>
                </div>
                <DropdownMenuSeparator />
                <div className={`px-2 py-1.5 text-xs font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  Current Filters Applied
                </div>
                <div className={`px-2 py-1 text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                  FY: {budgetData?.fiscal_year}
                </div>
                <div className={`px-2 py-1 text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                  Period: {TIME_PERIODS.find(p => p.value === timePeriod)?.label}
                </div>
                {selectedMonth && (
                  <div className={`px-2 py-1 text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                    Month: {MONTH_NAMES[selectedMonth]}
                  </div>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleExport('xlsx', 'full')}>
                  <Download className="w-4 h-4 mr-2" />
                  Full Report (xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('pdf', 'full')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Full Report (PDF)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Performance Info Bar */}
      <div className={`p-3 rounded-sm flex items-center gap-4 flex-wrap ${isDark ? 'bg-emerald-900/20 border border-emerald-800/50' : 'bg-emerald-50 border border-emerald-200'}`}>
        <TrendingUp className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
        <span className={`font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>Performance</span>
        <Badge variant="outline" className={isDark ? 'border-emerald-700 text-emerald-400' : 'border-emerald-300 text-emerald-700'}>
          {budgetData?.fiscal_year}
        </Badge>
        <span className={`text-sm ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}>
          {budgetData?.business_unit_name || 'All Business Units'}
        </span>
        <span className={`text-sm ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}>
          • {budgetData?.name}
        </span>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <SummaryCard label="Sales" budget={totals.sales.budget} actual={totals.sales.actual} isDark={isDark} />
        <SummaryCard label="Purchases" budget={totals.purchases.budget} actual={totals.purchases.actual} isDark={isDark} isExpense />
        <SummaryCard label="Gross Profit" budget={totals.grossProfit.budget} actual={totals.grossProfit.actual} isDark={isDark} highlight />
        <SummaryCard label="Opex" budget={totals.opex.budget} actual={totals.opex.actual} isDark={isDark} isExpense />
        <SummaryCard label="Operating Profit" budget={totals.operatingProfit.budget} actual={totals.operatingProfit.actual} isDark={isDark} />
        <SummaryCard label="Net Profit" budget={totals.netProfit.budget} actual={totals.netProfit.actual} isDark={isDark} highlight />
      </div>

      {/* Category Tables */}
      <div className="space-y-4">
        {Object.entries(groupedItems).map(([category, data]) => {
          const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS['Other Expenses'];
          const categoryBudget = calculateCategoryBudget(category);
          const categoryActual = calculateCategoryActual(category);
          const categoryVariance = categoryActual - categoryBudget;
          const isExpanded = expandedCategories.has(category);
          const mainAccounts = Object.entries(data.mainAccounts).filter(([_, ma]) => {
            const filteredSubs = filterZeroLines(ma.subAccounts);
            return filteredSubs.length > 0 || ma.header;
          });
          
          if (mainAccounts.length === 0) return null;
          if (hideZeroLines && categoryBudget === 0 && categoryActual === 0) return null;
          
          return (
            <Card key={category} className={`border shadow-none rounded-sm overflow-hidden ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
              <Collapsible open={isExpanded} onOpenChange={() => toggleCategory(category)}>
                <CollapsibleTrigger asChild>
                  <CardHeader className={`py-3 px-4 ${colors.bg} ${colors.border} border-b cursor-pointer hover:opacity-90`}>
                    <div className="flex items-center justify-between">
                      <CardTitle className={`font-display text-base font-medium flex items-center gap-2 ${colors.text}`}>
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        <Layers className="w-4 h-4" />
                        {category}
                      </CardTitle>
                      <div className="flex items-center gap-6 font-mono text-sm">
                        <div className="text-right">
                          <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Actual</span>
                          <p className={colors.text}>{formatCurrency(categoryActual)}</p>
                        </div>
                        <div className="text-right">
                          <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Budget</span>
                          <p className={colors.text}>{formatCurrency(categoryBudget)}</p>
                        </div>
                        <div className="text-right">
                          <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Variance</span>
                          <p className={categoryVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                            {categoryVariance >= 0 ? '+' : ''}{formatCurrency(categoryVariance)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}>
                          <th className="text-left py-2 px-4 font-medium w-16">Code</th>
                          <th className="text-left py-2 px-4 font-medium min-w-[200px]">Account</th>
                          {displayMonths.map(month => (
                            <th key={month} colSpan={showVarianceColumns ? 4 : 2} className="text-center py-2 px-2 font-medium border-l border-slate-200 dark:border-zinc-700">
                              {MONTH_NAMES[month].substring(0, 3)}
                            </th>
                          ))}
                          <th className="text-right py-2 px-3 font-medium border-l border-slate-200 dark:border-zinc-700">Total Actual</th>
                          <th className="text-right py-2 px-3 font-medium">Total Budget</th>
                          <th className="text-right py-2 px-3 font-medium">Variance</th>
                          <th className="text-center py-2 px-2 font-medium w-12">
                            <MessageSquare className="w-4 h-4 mx-auto text-slate-400" />
                          </th>
                        </tr>
                        <tr className={`text-xs ${isDark ? 'bg-zinc-800/30 text-zinc-500' : 'bg-slate-50/50 text-slate-400'}`}>
                          <th></th>
                          <th></th>
                          {displayMonths.map(month => (
                            <React.Fragment key={month}>
                              <th className="py-1 px-1 text-center border-l border-slate-200 dark:border-zinc-700">Act</th>
                              <th className="py-1 px-1 text-center">Bud</th>
                              {showVarianceColumns && (
                                <>
                                  <th className="py-1 px-1 text-center">Var</th>
                                  <th className="py-1 px-1 text-center">%</th>
                                </>
                              )}
                            </React.Fragment>
                          ))}
                          <th className="border-l border-slate-200 dark:border-zinc-700"></th>
                          <th></th>
                          <th></th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {mainAccounts.map(([accountCode, mainAccount]) => {
                          const filteredSubAccounts = filterZeroLines(mainAccount.subAccounts);
                          
                          return (
                          <React.Fragment key={accountCode}>
                            {filteredSubAccounts.map((item, idx) => {
                              const itemBudget = calculateItemBudget(item);
                              const itemActual = calculateItemActual(item);
                              const variance = itemActual - itemBudget;
                              
                              return (
                                <tr key={idx} className={`border-t ${isDark ? 'border-zinc-800 hover:bg-zinc-800/30' : 'border-slate-100 hover:bg-slate-50'}`}>
                                  <td className={`py-2 px-4 font-mono text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                                    {item.account_code}
                                  </td>
                                  <td className={`py-2 px-4 ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                                    {item.account_name}
                                  </td>
                                  {displayMonths.map(month => {
                                    const budgetVal = item[month] || 0;
                                    const actualValue = item[`${month}_actual`];
                                    const actualVal = actualValue !== undefined && actualValue !== null ? actualValue : 0;
                                    const monthVariance = actualVal - budgetVal;
                                    const monthPct = budgetVal !== 0 ? (actualVal / budgetVal * 100) : 0;
                                    return (
                                      <React.Fragment key={month}>
                                        <td className={`py-2 px-1 text-right font-mono text-xs border-l ${isDark ? 'border-zinc-700 text-zinc-300' : 'border-slate-200 text-slate-700'}`}>
                                          {actualVal !== 0 ? formatCurrency(actualVal) : '-'}
                                        </td>
                                        <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                                          {budgetVal !== 0 ? formatCurrency(budgetVal) : '-'}
                                        </td>
                                        {showVarianceColumns && (
                                          <>
                                            <td className={`py-2 px-1 text-right font-mono text-xs ${monthVariance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                              {budgetVal !== 0 || actualVal !== 0 ? formatCurrency(monthVariance) : '-'}
                                            </td>
                                            <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                                              {budgetVal !== 0 ? `${monthPct.toFixed(1)}%` : '-'}
                                            </td>
                                          </>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                  <td className={`py-2 px-3 text-right font-mono font-medium border-l ${isDark ? 'border-zinc-700 text-white' : 'border-slate-200 text-slate-900'}`}>
                                    {formatCurrency(itemActual)}
                                  </td>
                                  <td className={`py-2 px-3 text-right font-mono font-medium ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
                                    {formatCurrency(itemBudget)}
                                  </td>
                                  <td className={`py-2 px-3 text-right font-mono font-medium ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    <div className="flex items-center justify-end gap-1">
                                      {variance >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                      {variance >= 0 ? '+' : ''}{formatCurrency(variance)}
                                    </div>
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 rounded-sm"
                                      onClick={() => openCommentsPanel(item.account_code, item.account_name)}
                                      data-testid={`comment-btn-${item.account_code}`}
                                    >
                                      <MessageSquare className={`w-4 h-4 ${isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-400 hover:text-slate-600'}`} />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        )})}
                      </tbody>
                    </table>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
      </div>

      {/* Financial Summary - Bottom of Detail View with Monthly Breakdown */}
      <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
        <CardHeader className={`py-3 border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
          <CardTitle className="font-display text-base font-medium flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-blue-600" />
            Financial Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}>
                <th className={`py-2 px-4 text-left font-medium min-w-[160px] ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Category</th>
                {displayMonths.map(month => (
                  <th key={month} colSpan={4} className={`text-center py-2 px-1 font-medium border-l ${isDark ? 'border-zinc-700 text-zinc-300' : 'border-slate-200 text-slate-700'}`}>
                    {MONTH_NAMES[month].substring(0, 3)}
                  </th>
                ))}
                <th className={`py-2 px-3 text-right font-medium border-l ${isDark ? 'border-zinc-700 text-zinc-300' : 'border-slate-200 text-slate-700'}`}>Total Act</th>
                <th className={`py-2 px-3 text-right font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Total Bud</th>
                <th className={`py-2 px-3 text-right font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Variance</th>
                <th className={`py-2 px-3 text-right font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>%</th>
              </tr>
              <tr className={`text-xs ${isDark ? 'bg-zinc-800/30 text-zinc-500' : 'bg-slate-50/50 text-slate-400'}`}>
                <th></th>
                {displayMonths.map(month => (
                  <React.Fragment key={month}>
                    <th className={`py-1 px-1 text-center border-l ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>Act</th>
                    <th className="py-1 px-1 text-center">Bud</th>
                    <th className="py-1 px-1 text-center">Var</th>
                    <th className="py-1 px-1 text-center">%</th>
                  </React.Fragment>
                ))}
                <th className={`border-l ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}></th>
                <th></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {/* Sales Row */}
              <tr className={`border-t ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                <td className={`py-2 px-4 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Sales</td>
                {displayMonths.map(month => {
                  const actual = calculateCategoryMonthActual('Sales', month);
                  const budget = calculateCategoryMonthBudget('Sales', month);
                  const variance = actual - budget;
                  const pct = budget !== 0 ? (actual / budget * 100) : 0;
                  return (
                    <React.Fragment key={month}>
                      <td className={`py-2 px-1 text-right font-mono text-xs border-l ${isDark ? 'border-zinc-700 text-zinc-300' : 'border-slate-200 text-slate-700'}`}>
                        {formatCurrency(actual)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {formatCurrency(budget)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(variance)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {budget !== 0 ? `${pct.toFixed(1)}%` : '-'}
                      </td>
                    </React.Fragment>
                  );
                })}
                <td className={`py-2 px-3 text-right font-mono font-medium border-l ${isDark ? 'border-zinc-700 text-white' : 'border-slate-200 text-slate-900'}`}>{formatCurrency(totals.sales.actual)}</td>
                <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.sales.budget)}</td>
                <td className={`py-2 px-3 text-right font-mono ${totals.sales.actual - totals.sales.budget >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {totals.sales.actual - totals.sales.budget >= 0 ? '+' : ''}{formatCurrency(totals.sales.actual - totals.sales.budget)}
                </td>
                <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  {totals.sales.budget !== 0 ? `${(totals.sales.actual / totals.sales.budget * 100).toFixed(1)}%` : '-'}
                </td>
              </tr>
              {/* Purchases Row */}
              <tr className={`border-t ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                <td className={`py-2 px-4 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Less: Purchases</td>
                {displayMonths.map(month => {
                  const actual = calculateCategoryMonthActual('Purchases', month);
                  const budget = calculateCategoryMonthBudget('Purchases', month);
                  const variance = budget - actual; // For expenses, favorable is when actual < budget
                  const pct = budget !== 0 ? (actual / budget * 100) : 0;
                  return (
                    <React.Fragment key={month}>
                      <td className={`py-2 px-1 text-right font-mono text-xs border-l text-red-600 ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                        {formatCurrencyAsExpense(actual)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {formatCurrencyAsExpense(budget)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(variance)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {budget !== 0 ? `${pct.toFixed(1)}%` : '-'}
                      </td>
                    </React.Fragment>
                  );
                })}
                <td className={`py-2 px-3 text-right font-mono font-medium border-l text-red-600 ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>{formatCurrencyAsExpense(totals.purchases.actual)}</td>
                <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrencyAsExpense(totals.purchases.budget)}</td>
                <td className={`py-2 px-3 text-right font-mono ${totals.purchases.budget - totals.purchases.actual >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {totals.purchases.budget - totals.purchases.actual >= 0 ? '+' : ''}{formatCurrency(totals.purchases.budget - totals.purchases.actual)}
                </td>
                <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  {totals.purchases.budget !== 0 ? `${(totals.purchases.actual / totals.purchases.budget * 100).toFixed(1)}%` : '-'}
                </td>
              </tr>
              {/* Gross Profit Row */}
              <tr className={`border-t-2 ${isDark ? 'border-zinc-700 bg-emerald-900/10' : 'border-slate-200 bg-emerald-50'}`}>
                <td className={`py-2 px-4 font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>Gross Profit</td>
                {displayMonths.map(month => {
                  const salesAct = calculateCategoryMonthActual('Sales', month);
                  const purchasesAct = calculateCategoryMonthActual('Purchases', month);
                  const salesBud = calculateCategoryMonthBudget('Sales', month);
                  const purchasesBud = calculateCategoryMonthBudget('Purchases', month);
                  const gpAct = salesAct - purchasesAct;
                  const gpBud = salesBud - purchasesBud;
                  const variance = gpAct - gpBud;
                  const pct = gpBud !== 0 ? (gpAct / gpBud * 100) : 0;
                  return (
                    <React.Fragment key={month}>
                      <td className={`py-2 px-1 text-right font-mono text-xs border-l ${isDark ? 'border-zinc-700' : 'border-slate-200'} ${gpAct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(gpAct)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {formatCurrency(gpBud)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(variance)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {gpBud !== 0 ? `${pct.toFixed(1)}%` : '-'}
                      </td>
                    </React.Fragment>
                  );
                })}
                <td className={`py-2 px-3 text-right font-mono font-semibold border-l ${isDark ? 'border-zinc-700' : 'border-slate-200'} ${totals.grossProfit.actual >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(totals.grossProfit.actual)}</td>
                <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.grossProfit.budget)}</td>
                <td className={`py-2 px-3 text-right font-mono font-medium ${totals.grossProfit.actual - totals.grossProfit.budget >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {totals.grossProfit.actual - totals.grossProfit.budget >= 0 ? '+' : ''}{formatCurrency(totals.grossProfit.actual - totals.grossProfit.budget)}
                </td>
                <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  {totals.grossProfit.budget !== 0 ? `${(totals.grossProfit.actual / totals.grossProfit.budget * 100).toFixed(1)}%` : '-'}
                </td>
              </tr>
              {/* Overhead Row */}
              <tr className={`border-t ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                <td className={`py-2 px-4 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Less: Overhead</td>
                {displayMonths.map(month => {
                  const actual = calculateCategoryMonthActual('Overhead', month);
                  const budget = calculateCategoryMonthBudget('Overhead', month);
                  const variance = budget - actual; // For expenses
                  const pct = budget !== 0 ? (actual / budget * 100) : 0;
                  return (
                    <React.Fragment key={month}>
                      <td className={`py-2 px-1 text-right font-mono text-xs border-l text-red-600 ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                        {formatCurrencyAsExpense(actual)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {formatCurrencyAsExpense(budget)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(variance)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {budget !== 0 ? `${pct.toFixed(1)}%` : '-'}
                      </td>
                    </React.Fragment>
                  );
                })}
                <td className={`py-2 px-3 text-right font-mono font-medium border-l text-red-600 ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>{formatCurrencyAsExpense(totals.overhead.actual)}</td>
                <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrencyAsExpense(totals.overhead.budget)}</td>
                <td className={`py-2 px-3 text-right font-mono ${totals.overhead.budget - totals.overhead.actual >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {totals.overhead.budget - totals.overhead.actual >= 0 ? '+' : ''}{formatCurrency(totals.overhead.budget - totals.overhead.actual)}
                </td>
                <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  {totals.overhead.budget !== 0 ? `${(totals.overhead.actual / totals.overhead.budget * 100).toFixed(1)}%` : '-'}
                </td>
              </tr>
              {/* Salaries Row */}
              <tr className={`border-t ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                <td className={`py-2 px-4 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Less: Salaries</td>
                {displayMonths.map(month => {
                  const actual = calculateCategoryMonthActual('Salaries', month);
                  const budget = calculateCategoryMonthBudget('Salaries', month);
                  const variance = budget - actual; // For expenses
                  const pct = budget !== 0 ? (actual / budget * 100) : 0;
                  return (
                    <React.Fragment key={month}>
                      <td className={`py-2 px-1 text-right font-mono text-xs border-l text-red-600 ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                        {formatCurrencyAsExpense(actual)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {formatCurrencyAsExpense(budget)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(variance)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {budget !== 0 ? `${pct.toFixed(1)}%` : '-'}
                      </td>
                    </React.Fragment>
                  );
                })}
                <td className={`py-2 px-3 text-right font-mono font-medium border-l text-red-600 ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>{formatCurrencyAsExpense(totals.salaries.actual)}</td>
                <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrencyAsExpense(totals.salaries.budget)}</td>
                <td className={`py-2 px-3 text-right font-mono ${totals.salaries.budget - totals.salaries.actual >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {totals.salaries.budget - totals.salaries.actual >= 0 ? '+' : ''}{formatCurrency(totals.salaries.budget - totals.salaries.actual)}
                </td>
                <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  {totals.salaries.budget !== 0 ? `${(totals.salaries.actual / totals.salaries.budget * 100).toFixed(1)}%` : '-'}
                </td>
              </tr>
              {/* Total Opex Row */}
              <tr className={`border-t ${isDark ? 'border-zinc-700 bg-zinc-800/50' : 'border-slate-200 bg-slate-50'}`}>
                <td className={`py-2 px-4 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Total Opex</td>
                {displayMonths.map(month => {
                  const ovhAct = calculateCategoryMonthActual('Overhead', month);
                  const salAct = calculateCategoryMonthActual('Salaries', month);
                  const mgmtAct = calculateCategoryMonthActual('Management Fees', month);
                  const othAct = calculateCategoryMonthActual('Other Expenses', month);
                  const opexAct = ovhAct + salAct + mgmtAct + othAct;
                  const ovhBud = calculateCategoryMonthBudget('Overhead', month);
                  const salBud = calculateCategoryMonthBudget('Salaries', month);
                  const mgmtBud = calculateCategoryMonthBudget('Management Fees', month);
                  const othBud = calculateCategoryMonthBudget('Other Expenses', month);
                  const opexBud = ovhBud + salBud + mgmtBud + othBud;
                  const variance = opexBud - opexAct; // For expenses
                  const pct = opexBud !== 0 ? (opexAct / opexBud * 100) : 0;
                  return (
                    <React.Fragment key={month}>
                      <td className={`py-2 px-1 text-right font-mono text-xs border-l ${isDark ? 'border-zinc-700 text-white' : 'border-slate-200 text-slate-900'}`}>
                        {formatCurrencyAsExpense(opexAct)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {formatCurrencyAsExpense(opexBud)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(variance)}
                      </td>
                      <td className={`py-2 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {opexBud !== 0 ? `${pct.toFixed(1)}%` : '-'}
                      </td>
                    </React.Fragment>
                  );
                })}
                <td className={`py-2 px-3 text-right font-mono font-medium border-l ${isDark ? 'border-zinc-700 text-white' : 'border-slate-200 text-slate-900'}`}>{formatCurrencyAsExpense(totals.opex.actual)}</td>
                <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrencyAsExpense(totals.opex.budget)}</td>
                <td className={`py-2 px-3 text-right font-mono ${totals.opex.budget - totals.opex.actual >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {totals.opex.budget - totals.opex.actual >= 0 ? '+' : ''}{formatCurrency(totals.opex.budget - totals.opex.actual)}
                </td>
                <td className={`py-2 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  {totals.opex.budget !== 0 ? `${(totals.opex.actual / totals.opex.budget * 100).toFixed(1)}%` : '-'}
                </td>
              </tr>
              {/* Net Profit Row */}
              <tr className={`${isDark ? 'bg-emerald-900/20' : 'bg-emerald-50'}`}>
                <td className={`py-3 px-4 font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>Net Profit / (Loss)</td>
                {displayMonths.map(month => {
                  const salesAct = calculateCategoryMonthActual('Sales', month);
                  const purchasesAct = calculateCategoryMonthActual('Purchases', month);
                  const ovhAct = calculateCategoryMonthActual('Overhead', month);
                  const salAct = calculateCategoryMonthActual('Salaries', month);
                  const mgmtAct = calculateCategoryMonthActual('Management Fees', month);
                  const othAct = calculateCategoryMonthActual('Other Expenses', month);
                  const netAct = (salesAct - purchasesAct) + (ovhAct + salAct + mgmtAct + othAct);
                  const salesBud = calculateCategoryMonthBudget('Sales', month);
                  const purchasesBud = calculateCategoryMonthBudget('Purchases', month);
                  const ovhBud = calculateCategoryMonthBudget('Overhead', month);
                  const salBud = calculateCategoryMonthBudget('Salaries', month);
                  const mgmtBud = calculateCategoryMonthBudget('Management Fees', month);
                  const othBud = calculateCategoryMonthBudget('Other Expenses', month);
                  const netBud = (salesBud - purchasesBud) + (ovhBud + salBud + mgmtBud + othBud);
                  const variance = netAct - netBud;
                  const pct = netBud !== 0 ? (netAct / netBud * 100) : 0;
                  return (
                    <React.Fragment key={month}>
                      <td className={`py-3 px-1 text-right font-mono text-xs font-medium border-l ${isDark ? 'border-zinc-700' : 'border-slate-200'} ${netAct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(netAct)}
                      </td>
                      <td className={`py-3 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {formatCurrency(netBud)}
                      </td>
                      <td className={`py-3 px-1 text-right font-mono text-xs ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(variance)}
                      </td>
                      <td className={`py-3 px-1 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {netBud !== 0 ? `${pct.toFixed(1)}%` : '-'}
                      </td>
                    </React.Fragment>
                  );
                })}
                <td className={`py-3 px-3 text-right font-mono font-bold border-l ${isDark ? 'border-zinc-700' : 'border-slate-200'} ${totals.netProfit.actual >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(totals.netProfit.actual)}</td>
                <td className={`py-3 px-3 text-right font-mono font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{formatCurrency(totals.netProfit.budget)}</td>
                <td className={`py-3 px-3 text-right font-mono font-bold ${totals.netProfit.actual - totals.netProfit.budget >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {totals.netProfit.actual - totals.netProfit.budget >= 0 ? '+' : ''}{formatCurrency(totals.netProfit.actual - totals.netProfit.budget)}
                </td>
                <td className={`py-3 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  {totals.netProfit.budget !== 0 ? `${(totals.netProfit.actual / totals.netProfit.budget * 100).toFixed(1)}%` : '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Comments Side Panel */}
      <CommentsSidePanel
        isOpen={commentsPanelOpen}
        onClose={() => setCommentsPanelOpen(false)}
        budgetId={selectedBudgetId}
        accountCode={selectedAccount.code}
        accountName={selectedAccount.name}
        showVarianceComments={true}
      />
    </div>
  );
};

export default BudgetPage;
